import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { stepUp, stepUpMethod } from './api';

/**
 * Second-level authorisation before a high-impact action (backend/step_up.py): the person re-enters their
 * authenticator code, or their password when they have no two-factor sign-in. That gives a single-use token for
 * this one action, valid 2 minutes, which ``perform(token)`` sends with the request.
 */
export default function StepUpDialog({ open, action, title, summary, perform, onDone, onCancel, testid = 'step-up-dialog' }) {
  const [method, setMethod] = useState(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setValue('');
    setError(null);
    stepUpMethod().then((m) => setMethod(m?.method || 'password')).catch(() => setMethod('password'));
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const creds = method === 'totp' ? { code: value.trim() } : { password: value };
      const { token } = await stepUp(action, creds);
      const result = await perform(token);
      onDone?.(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const totp = method === 'totp';
  return (
    <ConfirmDialog open={open} title={title || 'Confirm it is you'} testid={testid} danger busy={busy}
      confirmLabel="Confirm and continue" confirmDisabled={!method || !value} onCancel={onCancel} onConfirm={confirm}>
      <p className="flex items-start gap-2">
        <ShieldCheck className="w-5 h-5 shrink-0 text-ink" aria-hidden />
        <span>{summary} This needs a second check: {totp
          ? 'enter the 6-digit code from your authenticator app.'
          : 'enter your password again.'}</span>
      </p>
      <label className="block">
        <span className="block text-xs uppercase tracking-overline text-ink-muted mb-1">{totp ? 'Authenticator code' : 'Your password'}</span>
        <input type={totp ? 'text' : 'password'} inputMode={totp ? 'numeric' : undefined}
          autoComplete={totp ? 'one-time-code' : 'current-password'} value={value} data-testid={`${testid}-input`}
          onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && value && !busy) confirm(); }}
          className="w-full rounded-lg border border-surface-border bg-surface px-3 py-2 text-ink" />
      </label>
      <p className="text-xs">The check works once, for this action only, and is recorded with your name.</p>
      {error && <p className="text-danger" data-testid={`${testid}-error`}>{error}</p>}
    </ConfirmDialog>
  );
}
